import React from "react";
import { useSimpleTableStable, useSimpleTableVolatile } from "../../contexts/SimpleTableContext";
import { getFieldValue } from "./tableCellFieldValue";
import { getCellRenderer } from "./TableCellRouter";
import { TableCellWrapper } from "./TableCellWrapper";
import type {
  BaseEntity,
  UniversalInlineEditHandler,
  EntityWithRelations,
  FieldValue,
} from "../../types";
import type { TableCellProps } from "./TableCell.types";

export function TableCell<TData extends BaseEntity>({
  fieldId,
  fieldConfig,
  item,
  cellIndex,
  totalCells,
  isTextareaField,
  dataRowIds,
  dataIndex,
}: TableCellProps<TData>) {
  // Cells read the NARROW slices, never the composition: `useSimpleTableContext`
  // re-renders on any chrome change (an infinite-scroll fetch, a re-rendered
  // toolbar, a page-size change) and a context change bypasses the `React.memo`
  // on this component — which is the whole reason it is memoized.
  const {
    config,
    callbacks,
    auth,
    hooks,
    onEdit,
    onView,
    onDelete,
    onInlineEdit,
    onOpenDetailModal,
    data,
  } = useSimpleTableStable<TData>();
  const { state, utils } = useSimpleTableVolatile<TData>();

  const fieldValue = getFieldValue(item as EntityWithRelations<TData>, fieldId);
  // Only primitive values can seed a simple "Filter by this value" from the
  // row context menu; objects/dates are left out (undefined attr).
  const primitiveFilterValue =
    typeof fieldValue === "string" ||
    typeof fieldValue === "number" ||
    typeof fieldValue === "boolean"
      ? String(fieldValue)
      : undefined;
  const cellLabel =
    typeof fieldConfig.label === "string" ? fieldConfig.label : String(fieldConfig.label);
  const isEditing = state.editingCell?.rowId === item.id && state.editingCell?.fieldId === fieldId;
  // NOT `onInlineEdit || (async () => {})`. That fallback handed the specialized
  // cell renderers (JSON / tags / line items / badge / title, which persist through
  // `persistFieldUpdate` rather than through `handleCellSaveCallback`) a handler
  // that resolved successfully and wrote nothing: the cell repainted with the new
  // value, the edit was never sent, and the next refetch silently restored the old
  // one. The ordinary editable-cell path never hit it because
  // `handleCellSaveCallback` already gates on `onInlineEdit` — which is why this
  // only ever bit the specialized cells. A missing handler is a table
  // misconfiguration, so it now says so instead of pretending the save worked
  // (NO_SWALLOWED_ERRORS); the throw surfaces through the cell's own save path.
  const inlineEditHandler = React.useCallback<UniversalInlineEditHandler>(
    async (payload) => {
      if (!onInlineEdit) {
        throw new Error(
          `SimpleTable(${config.entity}): a cell edit was saved but the table has no onInlineEdit handler, so the change cannot be persisted.`
        );
      }
      // The one conversion in this chain. `UniversalInlineEditHandler` is declared
      // as `(item: BaseEntity) => Promise<void>` while every caller actually sends
      // the PARTIAL payload the context's `onInlineEdit` expects
      // (`{ id, [fieldId]: value }` — see `persistFieldUpdate`). Reconciling the two
      // declarations touches the whole editable-cell chain and is tracked separately;
      // this keeps it to a single, named place instead of an inline cast.
      await onInlineEdit(payload as Record<string, FieldValue>);
    },
    [onInlineEdit, config.entity]
  );

  // Get the appropriate cell renderer based on field type
  const cellContent = getCellRenderer({
    fieldId,
    fieldValue,
    fieldConfig,
    item,
    isEditing,
    isTextareaField,
    config,
    state,
    callbacks,
    auth,
    data,
    inlineEditHandler,
    onOpenDetailModal,
    dataRowIds,
    dataIndex,
    hooks,
    onEdit,
    onView,
    onDelete,
  });

  return (
    <TableCellWrapper
      fieldId={fieldId}
      fieldConfig={fieldConfig}
      cellIndex={cellIndex}
      totalCells={totalCells}
      isTextareaField={isTextareaField}
      columnStyle={utils.getColumnStyleCallback(fieldId, fieldConfig)}
      fieldLabel={cellLabel}
      filterValue={primitiveFilterValue}
    >
      {cellContent}
    </TableCellWrapper>
  );
}

// Memoized wrapper for performance
export const MemoizedTableCell = React.memo(TableCell) as typeof TableCell;
